/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Fragment, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';

import { ENTITY_TYPE_MAP, TP } from 'constants/index';

import { getCityCountry } from 'utils/globals';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { getEntityPerformedDates, getProductionCredits } from 'utils/productions';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import CreditsWorks from '../CreditsWorks';
import GenreTags from '../GenreTags';
import ProductionPeriod from '../ProductionPeriod';

import classes from './ExpandedView.module.scss';
import RoleEntityBlock from './RoleEntityBlock';
import VenuesAndDates from './VenuesAndDates';

const getTrackingDataForClick = (trackingData, item) => ({
  ...trackingData,
  entityType: item?.entityType,
  entityId: item?.entity?.id,
  entityName: item?.entity?.name,
});

const CoProducersEnsembles = ({ ensembles, coProducers, trackingData, handleRoleSelect }) => (
  <div className={classes.coProducersEnsembles}>
    <RoleEntityBlock
      item={coProducers}
      trackingData={trackingData}
      styles={{ roleEntityDetails: classes.roleEntityDetails }}
      handleRoleSelect={handleRoleSelect}
    />
    <span className={classes.tilde}>~</span>
    {ensembles?.map((item, i) => (
      <RoleEntityBlock
        item={item}
        key={i}
        trackingData={trackingData}
        styles={{ roleEntityDetails: classes.roleEntityDetails }}
        handleRoleSelect={handleRoleSelect}
      />
    ))}
  </div>
);

const ExpandedView = ({
  production,
  onClick,
  isExpanded,
  entity,
  trackingData,
  entityType,
  inline,
  showExpandedSlug,
  handleRoleSelect,
  isJumpInSlug,
  selectedWorkId,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate } = usePageContext();
  const { isJumpInSelectProductionsModalOpen } = useDialogs();
  const [workIds, setWorkIds] = useState([]);

  const { data: castCrewData, isLoading } = useQuery(
    queries.getWorkRoles({
      filters: {
        work_id: workIds,
      },
      queryConfig: { enabled: !!workIds?.length > 0 && isJumpInSlug },
    }),
  );

  useEffect(() => {
    if (isJumpInSelectProductionsModalOpen) {
      const ids = production?.productionWorks?.map(item => item?.work?.id);
      setWorkIds(ids);
    }
  }, [isJumpInSelectProductionsModalOpen]);

  const productionCredits = useMemo(
    () =>
      getProductionCredits(production, {
        entityId: entity?.id,
        entityType,
        workId: selectedWorkId || undefined,
        ...(!isLoading && Object.keys(castCrewData)?.length > 0 && !selectedWorkId ? { castCrewData } : {}),
      }),
    [production, entity?.id, entityType, selectedWorkId, isLoading, castCrewData],
  );

  const {
    directors,
    conductors,
    cast,
    crew,
    instrumentalists,
    producers,
    coProducers,
    ensembles,
    others,
    otherInstrumentalists,
  } = productionCredits || {};

  const { artistPerformedDates, total } = useMemo(() => getEntityPerformedDates(production, entity?.id), []);

  return (
    <div
      className={classnames(classes.expandedView, {
        [classes.active]: !!isExpanded,
        [classes.inline]: inline,
        [classes.expandedSlug]: !!showExpandedSlug,
      })}
      onClick={onClick}
    >
      <LinkButton
        styles={{ root: classnames(classes.arrowIcon, { [classes.expandedLine]: !!isExpanded }) }}
        variant="text"
        leftIcon={<SpriteIcon icon="keyboard_arrow_up" size={12} />}
        disableHover
        disableUnderline
        skipTracking
      />
      <div className={classes.expandedView__block}>
        <div className={classes.expandedView__block_left}>
          <div className={classes.tags}>
            <GenreTags production={production} isColumnView isExpanded styles={{ tagsList: classes.tagsList }} />
          </div>
          <div className={classes.creditWorks}>
            <CreditsWorks
              production={production}
              entityId={entity?.id}
              trackingData={trackingData}
              entityType={entityType}
              isExpanded
              isJumpInSlug={isJumpInSlug}
            />
          </div>
        </div>
        <div className={classes.expandedView__block_right}>
          <div className={classes.datesOrgVenues}>
            <div className={classes.rowOne}>
              <div className={classes.periodDates}>
                <div className={classes.period}>
                  <ProductionPeriod production={production} trackingData={trackingData} entity={entity} />
                </div>
                {entityType === ENTITY_TYPES.PROFILE && (
                  <Typography size={12} className={classes.dates}>
                    {`(${artistPerformedDates} / ${total} ${t(`${TP}.m_DATES`)})`}
                  </Typography>
                )}
              </div>
              <div className={classes.orgVenues}>
                <div className={classes.org}>
                  {producers?.data?.map(producer => (
                    <Fragment key={`${producer?.entity?.id}`}>
                      {producer?.entity?.id && (
                        <>
                          <Typography size="12" weight="medium">
                            <LinkButton
                              variant="text"
                              styles={{ root: classes.orgName }}
                              isLink
                              {...navigate?.getLinkProps({
                                entity: producer.entity,
                                entityType: ENTITY_TYPE_MAP[producer?.entityType],
                              })}
                              onClick={getTrackingDataForClick(trackingData, producer)}
                              trackingData={{
                                ...trackingData,
                                subComponent: SUB_COMPONENTS.PRODUCER,
                              }}
                            >
                              <EntityName
                                key={producer.entity.id}
                                entityType={ENTITY_TYPE_MAP[producer?.entityType]}
                                entity={{ id: producer.entity.id, name: producer?.entity?.name }}
                                isRaw
                                trackingData={trackingData}
                              />
                            </LinkButton>
                          </Typography>
                          <Typography size="12" className={classes.producerCityCountry}>
                            {getCityCountry(producer?.entity)}
                          </Typography>
                        </>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className={classes.rowTwo}>
              <CoProducersEnsembles
                trackingData={trackingData}
                ensembles={ensembles}
                coProducers={coProducers}
                handleRoleSelect={handleRoleSelect}
              />
            </div>
            <div className={classes.rowThree}>
              <VenuesAndDates
                production={production}
                entityId={entity?.id}
                entityType={entityType}
                trackingData={trackingData}
              />
            </div>
          </div>
          <div className={classes.allCredits}>
            <div className={classes.directorsConductorsBlock}>
              <div className={classes.directorsConductorsBlock__directors}>
                <RoleEntityBlock
                  item={directors}
                  trackingData={trackingData}
                  styles={{ roleEntityDetails: classes.directorItem }}
                  handleRoleSelect={handleRoleSelect}
                />
              </div>
              <div className={classes.directorsConductorsBlock__conductors}>
                <RoleEntityBlock
                  item={conductors}
                  trackingData={trackingData}
                  styles={{ roleEntityDetails: classes.conductorItem }}
                  handleRoleSelect={handleRoleSelect}
                />
              </div>
            </div>
            <div className={classes.crewCastInstrumentalist}>
              <div className={classes.crewCastInstrumentalist__crewColumn}>
                <div className={classes.crew}>
                  {crew?.length > 0 && (
                    <>
                      <Typography size={12} weight="bold" className={classes.title}>
                        {t(`${TP}.FN_CREW_TITLE`)}
                      </Typography>
                      <div className={classes.crew__members}>
                        {crew?.sort((a, b) => {
                          // Sort crew sections by profession order (ascending)
                          const getMinOrder = (section) => {
                            if (!section?.data?.length) return 99999;
                            return Math.min(...section.data.map(person => person?.profession?.order ?? 99999));
                          };
                          
                          const orderA = getMinOrder(a);
                          const orderB = getMinOrder(b);
                          return orderA - orderB;
                        })?.map((item, i) => (
                          <div className={classes.item} key={i}>
                            <RoleEntityBlock
                              item={item}
                              key={i}
                              trackingData={trackingData}
                              styles={{ roleEntityDetails: classes.castCrewDetails }}
                              handleRoleSelect={handleRoleSelect}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className={classes.crewCastInstrumentalist__castColumn}>
                <div className={classes.cast}>
                  {cast?.length > 0 && (
                    <>
                      <Typography size={12} weight="bold" className={classes.title}>
                        {t(`${TP}.FN_CAST`)}
                      </Typography>
                      <div className={classes.cast__members}>
                        {cast?.map((item, i) => (
                          <div className={classes.item} key={i}>
                            <RoleEntityBlock
                              item={item}
                              key={i}
                              trackingData={trackingData}
                              styles={{ roleEntityDetails: classes.castCrewDetails }}
                              handleRoleSelect={handleRoleSelect}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {others?.length > 0 && (
                  <div className={classes.otherRole}>
                    <Typography size={12} weight="bold" className={classes.title}>
                      {t(`${TP}.FN_OTHER_ROLE`)}
                    </Typography>
                    <div className={classes.otherRole__members}>
                      {others?.map((item, i) => (
                        <div className={classes.item} key={i}>
                          <RoleEntityBlock
                            item={item}
                            key={i}
                            trackingData={trackingData}
                            styles={{ roleEntityDetails: classes.castCrewDetails }}
                            handleRoleSelect={handleRoleSelect}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.crewCastInstrumentalist__instrumentalistColumn}>
                {instrumentalists?.length > 0 && (
                  <div className={classes.instrumentalists}>
                    <Typography size={12} weight="bold" className={classes.title}>
                      {t(`${TP}.FN_INDUSTRY_PRO_INSTRUMENTALISTS_LABEL`)}
                    </Typography>
                    {instrumentalists?.length > 0 && (
                      <>
                        {instrumentalists?.map((item, i) => (
                          <div className={classes.item} key={i}>
                            <RoleEntityBlock
                              item={item}
                              key={i}
                              trackingData={trackingData}
                              styles={{ roleEntityDetails: classes.castCrewDetails }}
                              handleRoleSelect={handleRoleSelect}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                <div className={classes.otherInstrumentalist}>
                  {otherInstrumentalists?.length > 0 && (
                    <>
                      <Typography size={12} weight="bold" className={classes.title}>
                        {t(`${TP}.OTHER_INSTRUMENTALISTS`)}
                      </Typography>
                      {otherInstrumentalists?.map((item, i) => (
                        <div className={classes.item} key={i}>
                          <RoleEntityBlock
                            item={item}
                            trackingData={trackingData}
                            styles={{ roleEntityDetails: classes.insDetails }}
                            handleRoleSelect={handleRoleSelect}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpandedView;
